<!-- ER图显示组件 - SVG版本 -->
<template>
  <div class="diagram-output">
    <div class="toolbar">
      <div class="toolbar-left">
        <div class="canvas-hint-toolbar">💡 提示：节点可拖拽 · 画布可缩放 · 双击文本可编辑</div>
      </div>
      <div class="toolbar-right">
        <button class="tool-btn btn-purple" @click="openEntitySelectDialog">
          <i class="el-icon-s-operation"></i>筛选实体
        </button>
        <button class="tool-btn btn-orange" @click="toggleFullscreen">
          <i class="el-icon-full-screen"></i>全屏展示
        </button>
        <button class="tool-btn btn-blue" @click="openExportDialog">
          <i class="el-icon-download"></i>导出ER图
        </button>
      </div>
    </div>

    <!-- 空状态提示 -->
    <div v-if="entities.length === 0" class="empty-state">
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="64" height="64">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
      </svg>
      <p class="empty-title">暂无ER图数据</p>
      <p class="empty-description">请在左侧输入SQL建表语句，然后点击"单个 ER 图"按钮生成</p>
    </div>

    <!-- 图表视图 -->
    <div v-else class="diagram-wrapper">
      <div class="diagram-container" ref="diagramContainer">
        <svg 
          class="diagram-svg"
          :viewBox="zoomedViewBox"
          preserveAspectRatio="xMidYMid meet"
          @mousedown="startPan"
          @mousemove="onDrag"
          @mouseup="endDrag"
          @mouseleave="endDrag"
          @wheel.prevent="handleWheel"
          :class="{ 'panning': isPanning }">
          
          <!-- 连接线 - 放在最底层 -->
          <g class="connections">
            <line
              v-for="link in links"
              :key="link.id"
              v-show="isLinkVisible(link)"
              :x1="link.x1"
              :y1="link.y1"
              :x2="link.x2"
              :y2="link.y2"
              stroke="#000000"
              stroke-width="1"
              fill="none">
            </line>
          </g>

          <!-- 实体节点 -->
          <g class="entities">
            <g v-for="entity in entities" :key="entity.key" v-show="entity.visible">
              <rect
                :x="entity.x"
                :y="entity.y"
                :width="entity.width"
                :height="entity.height"
                stroke="#000000"
                stroke-width="1"
                fill="white"
                @mousedown.stop="startDragNode($event, entity)"
                @dblclick.stop="startEditText(entity)"
                class="draggable-node">
              </rect>
              <text
                v-if="editingNode !== entity"
                :x="entity.x + entity.width / 2"
                :y="entity.y + entity.height / 2"
                text-anchor="middle"
                dominant-baseline="middle"
                font-family="宋体"
                font-size="22"
                fill="#000000"
                @mousedown.stop="startDragNode($event, entity)"
                @dblclick.stop="startEditText(entity)"
                pointer-events="none">
                {{ entity.name }}
              </text>
              <foreignObject
                v-if="editingNode === entity"
                :x="entity.x"
                :y="entity.y"
                :width="entity.width"
                :height="entity.height">
                <input
                  type="text"
                  v-model="editingText"
                  @blur="finishEdit"
                  @keyup.enter="finishEdit"
                  @keyup.esc="cancelEdit"
                  ref="editInput"
                  class="edit-input"
                  style="width: 100%; height: 100%; text-align: center; font-size: 22px; font-family: 宋体; border: 2px solid #409eff; outline: none;">
              </foreignObject>
              <!-- 调整大小手柄 -->
              <circle
                v-if="selectedNode === entity"
                :cx="entity.x + entity.width"
                :cy="entity.y + entity.height"
                r="6"
                fill="#409eff"
                stroke="white"
                stroke-width="2"
                @mousedown.stop="startResize($event, entity)"
                class="resize-handle">
              </circle>
            </g>
          </g>

          <!-- 属性节点 -->
          <g class="attributes">
            <g v-for="attr in attributes" :key="attr.key" v-show="attr.visible">
              <ellipse
                :cx="attr.x"
                :cy="attr.y"
                :rx="attr.rx"
                :ry="attr.ry"
                stroke="#000000"
                stroke-width="1"
                fill="white"
                @mousedown.stop="startDragNode($event, attr)"
                @dblclick.stop="startEditText(attr)"
                class="draggable-node">
                <title>{{ attr.name }}</title>
              </ellipse>
              <text
                v-if="editingNode !== attr"
                :x="attr.x"
                :y="attr.y"
                text-anchor="middle"
                dominant-baseline="middle"
                font-family="宋体"
                font-size="20"
                fill="#333"
                @mousedown.stop="startDragNode($event, attr)"
                @dblclick.stop="startEditText(attr)"
                pointer-events="none">
                {{ truncateText(attr.name, 6) }}
              </text>
              <foreignObject
                v-if="editingNode === attr"
                :x="attr.x - attr.rx"
                :y="attr.y - attr.ry"
                :width="attr.rx * 2"
                :height="attr.ry * 2">
                <input
                  type="text"
                  v-model="editingText"
                  @blur="finishEdit"
                  @keyup.enter="finishEdit"
                  @keyup.esc="cancelEdit"
                  ref="editInput"
                  class="edit-input"
                  style="width: 100%; height: 100%; text-align: center; font-size: 20px; font-family: 宋体; border: 2px solid #409eff; outline: none; border-radius: 50%;">
              </foreignObject>
              <!-- 调整大小手柄 -->
              <circle
                v-if="selectedNode === attr"
                :cx="attr.x + attr.rx"
                :cy="attr.y"
                r="6"
                fill="#409eff"
                stroke="white"
                stroke-width="2"
                @mousedown.stop="startResize($event, attr)"
                class="resize-handle">
              </circle>
              <!-- 主键下划线 -->
              <line
                v-if="attr.isPrimaryKey && editingNode !== attr"
                :x1="attr.x - (truncateText(attr.name, 6).length * 8)"
                :y1="attr.y + 8"
                :x2="attr.x + (truncateText(attr.name, 6).length * 8)"
                :y2="attr.y + 8"
                stroke="#333"
                stroke-width="1"
                pointer-events="none">
              </line>
            </g>
          </g>
        </svg>
      </div>
    </div>

    <ExportDialog
      :visible.sync="isExportDialogVisible"
      @confirm="handleExportConfirm"
    />

    <!-- 实体选择对话框 -->
    <el-dialog
      :visible.sync="isEntitySelectVisible"
      title="选择要显示的实体"
      width="400px">
      <div class="entity-select-header">
        <el-button type="text" @click="selectAllEntities">全选</el-button>
        <el-button type="text" @click="invertSelection">反选</el-button>
        <el-button type="text" @click="selectedEntities = []">清空</el-button>
      </div>
      <el-checkbox-group v-model="selectedEntities" class="entity-checkbox-group">
        <el-checkbox
          v-for="entity in availableEntities"
          :key="entity.key"
          :label="entity.key">
          {{ entity.name }}
        </el-checkbox>
      </el-checkbox-group>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="isEntitySelectVisible = false">取消</el-button>
          <el-button type="primary" @click="applyEntityFilter">确定</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import ExportDialog from './ExportDialog.vue'

export default {
  name: 'ErDiagram',
  components: { ExportDialog },
  data() {
    return {
      svgWidth: 3000,
      svgHeight: 3000,
      entities: [],
      attributes: [],
      attributeInitialPositions: [], // 存储属性相对于实体的初始偏移
      links: [],
      isExportDialogVisible: false,
      isFullscreen: false,
      isEntitySelectVisible: false,
      availableEntities: [],
      selectedEntities: [],
      draggingNode: null,
      dragStartX: 0,
      dragStartY: 0,
      mouseStartX: 0,
      mouseStartY: 0,
      isPanning: false,
      panStartX: 0,
      panStartY: 0,
      // 缩放和平移
      zoomLevel: 1,
      panX: 0,
      panY: 0,
      minZoom: 0.1,
      maxZoom: 10,
      editingNode: null,
      editingText: '',
      resizingNode: null,
      resizeStartX: 0,
      resizeStartY: 0,
      resizeStartWidth: 0,
      resizeStartHeight: 0,
      resizeStartRx: 0,
      resizeStartRy: 0,
      selectedNode: null // 当前选中的节点
    }
  },
  computed: {
    zoomedViewBox() {
      const width = this.svgWidth / this.zoomLevel
      const height = this.svgHeight / this.zoomLevel
      const x = -this.panX / this.zoomLevel
      const y = -this.panY / this.zoomLevel
      return `${x} ${y} ${width} ${height}`
    }
  },
  mounted() {
    document.addEventListener('fullscreenchange', () => {
      this.isFullscreen = !!document.fullscreenElement
    })
  },
  methods: {
    updateDiagram(tables) {
      this.entities = []
      this.attributes = []
      this.attributeInitialPositions = []
      this.links = []

      const COLS = 3
      const CELL_W = 1000
      const CELL_H = 900
      const ATTR_RADIUS = 220
      const TOP_MARGIN = 300 // 增加顶部边距

      // 计算实体位置
      tables.forEach((table, i) => {
        const col = i % COLS
        const row = Math.floor(i / COLS)
        const x = col * CELL_W + 100
        const y = row * CELL_H + TOP_MARGIN

        // 添加实体节点
        this.entities.push({
          key: table.tableName,
          name: this.removeQuotes(table.comment) || table.tableName,
          x: x,
          y: y,
          width: 200,
          height: 50,
          visible: true
        })

        // 添加属性节点
        const cols = table.columns || []
        const n = cols.length
        if (n > 0) {
          const startAngle = -Math.PI / 2
          const minRadius = Math.max(ATTR_RADIUS, n * 30)
          const angleStep = (2 * Math.PI) / n
          const entityCenterX = x + 100
          const entityCenterY = y + 25

          cols.forEach((col, idx) => {
            const angle = startAngle + idx * angleStep
            const ax = entityCenterX + minRadius * Math.cos(angle)
            const ay = entityCenterY + minRadius * Math.sin(angle)
            const attrKey = `${table.tableName}-${col.name}`
            const attrName = this.removeQuotes(col.comment) || col.name

            // 根据截断后的文字长度动态计算椭圆大小
            const displayName = this.truncateText(attrName, 6)
            const textLength = displayName.length
            const rx = Math.max(60, textLength * 12 + 20) // 横向半径根据显示文字长度
            const ry = 30 // 纵向半径固定

            this.attributes.push({
              key: attrKey,
              name: attrName,
              x: ax,
              y: ay,
              rx: rx,
              ry: ry,
              isPrimaryKey: table.primaryKeys.includes(col.name),
              entityKey: table.tableName,
              visible: true,
              fullName: attrName // 保存完整名称用于编辑
            })

            // 存储属性相对于实体中心的偏移量
            this.attributeInitialPositions.push({
              key: attrKey,
              offsetX: minRadius * Math.cos(angle),
              offsetY: minRadius * Math.sin(angle)
            })

            // 计算连接线的起点（矩形边缘）
            const edgePoint = this.getRectangleEdgePoint(
              x, y, 200, 50,
              entityCenterX, entityCenterY,
              ax, ay
            )

            // 添加连接线
            this.links.push({
              id: `link-${attrKey}`,
              x1: edgePoint.x,
              y1: edgePoint.y,
              x2: ax,
              y2: ay
            })
          })
        }
      })
    },

    removeQuotes(str) {
      if (!str) return str
      return str.replace(/^'|'$/g, '')
    },

    truncateText(text, maxLength = 6) {
      if (!text) return text
      if (text.length <= maxLength) return text
      return text.substring(0, maxLength) + '...'
    },

    isLinkVisible(link) {
      // 检查连接线对应的属性和实体是否都可见
      const attrKey = link.id.replace('link-', '')
      const attr = this.attributes.find(a => a.key === attrKey)
      if (!attr || !attr.visible) return false
      
      const entity = this.entities.find(e => e.key === attr.entityKey)
      return entity && entity.visible
    },

    startDragNode(event, node) {
      event.stopPropagation()
      this.selectedNode = node // 选中节点
      this.draggingNode = node
      
      // 记录节点当前位置和鼠标位置的偏移
      if (node.width) {
        // 实体节点
        this.dragStartX = node.x
        this.dragStartY = node.y
      } else {
        // 属性节点
        this.dragStartX = node.x
        this.dragStartY = node.y
      }
      
      // 记录鼠标起始位置（SVG坐标系）
      const svg = this.$el.querySelector('.diagram-svg')
      const pt = svg.createSVGPoint()
      pt.x = event.clientX
      pt.y = event.clientY
      const svgP = pt.matrixTransform(svg.getScreenCTM().inverse())
      this.mouseStartX = svgP.x
      this.mouseStartY = svgP.y
    },

    startPan(event) {
      // 只有在没有拖拽节点时才允许拖拽画布
      if (!this.draggingNode && event.target.classList.contains('diagram-svg')) {
        this.selectedNode = null // 取消选中
        this.isPanning = true
        this.panStartX = event.clientX
        this.panStartY = event.clientY
      }
    },

    onDrag(event) {
      if (this.resizingNode) {
        // 调整节点大小
        const svg = this.$el.querySelector('.diagram-svg')
        const pt = svg.createSVGPoint()
        pt.x = event.clientX
        pt.y = event.clientY
        const svgP = pt.matrixTransform(svg.getScreenCTM().inverse())
        
        if (this.resizingNode.width) {
          // 实体节点 - 调整宽度和高度
          const newWidth = Math.max(100, svgP.x - this.resizingNode.x)
          const newHeight = Math.max(30, svgP.y - this.resizingNode.y)
          this.resizingNode.width = newWidth
          this.resizingNode.height = newHeight
        } else {
          // 属性节点 - 调整椭圆半径
          const dx = svgP.x - this.resizingNode.x
          const dy = svgP.y - this.resizingNode.y
          this.resizingNode.rx = Math.max(30, Math.abs(dx))
          this.resizingNode.ry = Math.max(20, Math.abs(dy))
        }
        
        this.updateLinks()
      } else if (this.draggingNode) {
        // 获取当前鼠标在SVG坐标系中的位置
        const svg = this.$el.querySelector('.diagram-svg')
        const pt = svg.createSVGPoint()
        pt.x = event.clientX
        pt.y = event.clientY
        const svgP = pt.matrixTransform(svg.getScreenCTM().inverse())
        
        // 计算偏移量
        const dx = svgP.x - this.mouseStartX
        const dy = svgP.y - this.mouseStartY
        
        // 更新节点位置
        this.draggingNode.x = this.dragStartX + dx
        this.draggingNode.y = this.dragStartY + dy

        // 如果拖拽的是实体节点，同时移动其关联的属性节点
        if (this.draggingNode.width) {
          // 这是实体节点
          const entityKey = this.draggingNode.key
          this.attributes.forEach(attr => {
            if (attr.entityKey === entityKey) {
              // 找到该属性的初始位置（相对于实体的偏移）
              const attrInitialData = this.attributeInitialPositions.find(a => a.key === attr.key)
              if (attrInitialData) {
                attr.x = this.draggingNode.x + this.draggingNode.width / 2 + attrInitialData.offsetX
                attr.y = this.draggingNode.y + this.draggingNode.height / 2 + attrInitialData.offsetY
              }
            }
          })
        }

        // 更新连接线
        this.updateLinks()
      } else if (this.isPanning) {
        // 拖拽画布 - 大幅提升速度
        const dx = event.clientX - this.panStartX
        const dy = event.clientY - this.panStartY
        this.panX += dx * 5
        this.panY += dy * 5
        this.panStartX = event.clientX
        this.panStartY = event.clientY
      }
    },

    endDrag() {
      // 如果拖拽的是属性节点，更新其相对于实体的偏移量
      if (this.draggingNode && !this.draggingNode.width) {
        // 这是属性节点
        const attr = this.draggingNode
        const entity = this.entities.find(e => e.key === attr.entityKey)
        if (entity) {
          const entityCenterX = entity.x + entity.width / 2
          const entityCenterY = entity.y + entity.height / 2
          
          // 更新该属性的偏移量
          const attrInitialData = this.attributeInitialPositions.find(a => a.key === attr.key)
          if (attrInitialData) {
            attrInitialData.offsetX = attr.x - entityCenterX
            attrInitialData.offsetY = attr.y - entityCenterY
          }
        }
      }
      
      this.draggingNode = null
      this.resizingNode = null
      this.isPanning = false
    },

    handleWheel(event) {
      const delta = event.deltaY > 0 ? 0.9 : 1.1
      const newZoom = this.zoomLevel * delta
      
      if (newZoom >= this.minZoom && newZoom <= this.maxZoom) {
        const oldZoom = this.zoomLevel
        this.zoomLevel = newZoom
        
        // 以画布中心为缩放中心
        const zoomRatio = newZoom / oldZoom
        this.panX = this.panX * zoomRatio
        this.panY = this.panY * zoomRatio
      }
    },

    updateLinks() {
      this.links.forEach(link => {
        const attrKey = link.id.replace('link-', '')
        const attr = this.attributes.find(a => a.key === attrKey)
        if (attr) {
          const entityKey = attr.entityKey
          const entity = this.entities.find(e => e.key === entityKey)
          if (entity) {
            // 计算实体中心点
            const entityCenterX = entity.x + entity.width / 2
            const entityCenterY = entity.y + entity.height / 2
            
            // 计算从实体中心到属性的方向
            const dx = attr.x - entityCenterX
            const dy = attr.y - entityCenterY
            
            // 计算连接点在矩形边缘上的位置
            const edgePoint = this.getRectangleEdgePoint(
              entity.x, entity.y, entity.width, entity.height,
              entityCenterX, entityCenterY,
              attr.x, attr.y
            )
            
            link.x1 = edgePoint.x
            link.y1 = edgePoint.y
            link.x2 = attr.x
            link.y2 = attr.y
          }
        }
      })
    },
    
    /**
     * 计算从矩形中心到目标点的射线与矩形边缘的交点
     */
    getRectangleEdgePoint(rectX, rectY, rectWidth, rectHeight, centerX, centerY, targetX, targetY) {
      const dx = targetX - centerX
      const dy = targetY - centerY
      
      // 避免除以零
      if (dx === 0 && dy === 0) {
        return { x: centerX, y: centerY }
      }
      
      // 计算射线与矩形四条边的交点
      const halfWidth = rectWidth / 2
      const halfHeight = rectHeight / 2
      
      // 使用参数方程：点 = 中心 + t * 方向
      // 计算与四条边的交点参数 t
      let tMin = Infinity
      let edgeX = centerX
      let edgeY = centerY
      
      // 右边: x = centerX + halfWidth
      if (dx > 0) {
        const t = halfWidth / dx
        const y = centerY + t * dy
        if (Math.abs(y - centerY) <= halfHeight && t < tMin) {
          tMin = t
          edgeX = centerX + halfWidth
          edgeY = y
        }
      }
      
      // 左边: x = centerX - halfWidth
      if (dx < 0) {
        const t = -halfWidth / dx
        const y = centerY + t * dy
        if (Math.abs(y - centerY) <= halfHeight && t < tMin) {
          tMin = t
          edgeX = centerX - halfWidth
          edgeY = y
        }
      }
      
      // 下边: y = centerY + halfHeight
      if (dy > 0) {
        const t = halfHeight / dy
        const x = centerX + t * dx
        if (Math.abs(x - centerX) <= halfWidth && t < tMin) {
          tMin = t
          edgeX = x
          edgeY = centerY + halfHeight
        }
      }
      
      // 上边: y = centerY - halfHeight
      if (dy < 0) {
        const t = -halfHeight / dy
        const x = centerX + t * dx
        if (Math.abs(x - centerX) <= halfWidth && t < tMin) {
          tMin = t
          edgeX = x
          edgeY = centerY - halfHeight
        }
      }
      
      return { x: edgeX, y: edgeY }
    },

    startEditText(node) {
      this.editingNode = node
      this.editingText = node.name
      this.$nextTick(() => {
        const input = this.$refs.editInput
        if (input) {
          if (Array.isArray(input)) {
            input[0].focus()
            input[0].select()
          } else {
            input.focus()
            input.select()
          }
        }
      })
    },

    finishEdit() {
      if (this.editingNode && this.editingText.trim()) {
        this.editingNode.name = this.editingText.trim()
        
        // 根据新文字长度调整节点大小
        if (this.editingNode.width) {
          // 实体节点 - 调整矩形宽度
          const newWidth = Math.max(200, this.editingText.trim().length * 22 + 40)
          const oldCenterX = this.editingNode.x + this.editingNode.width / 2
          this.editingNode.width = newWidth
          this.editingNode.x = oldCenterX - newWidth / 2 // 保持中心点不变
        } else {
          // 属性节点 - 根据截断后的文字长度调整椭圆横向半径
          const displayName = this.truncateText(this.editingText.trim(), 6)
          this.editingNode.rx = Math.max(60, displayName.length * 12 + 20)
        }
        
        // 更新连接线
        this.updateLinks()
      }
      this.editingNode = null
      this.editingText = ''
    },

    cancelEdit() {
      this.editingNode = null
      this.editingText = ''
    },

    startResize(event, node) {
      this.resizingNode = node
      const svg = this.$el.querySelector('.diagram-svg')
      const pt = svg.createSVGPoint()
      pt.x = event.clientX
      pt.y = event.clientY
      const svgP = pt.matrixTransform(svg.getScreenCTM().inverse())
      
      this.resizeStartX = svgP.x
      this.resizeStartY = svgP.y
      
      if (node.width) {
        this.resizeStartWidth = node.width
        this.resizeStartHeight = node.height
      } else {
        this.resizeStartRx = node.rx
        this.resizeStartRy = node.ry
      }
    },

    openExportDialog() { this.isExportDialogVisible = true },

    handleExportConfirm(options) {
      if (options.format === 'drawio') {
        this.exportToDrawio()
      } else {
        this.exportToImage(options)
      }
    },

    exportToDrawio() {
      // 生成 draw.io XML 格式
      let xml = '<?xml version="1.0" encoding="UTF-8"?>\n'
      xml += '<mxfile host="app.diagrams.net" modified="' + new Date().toISOString() + '" agent="ER Diagram Generator" version="21.0.0" type="device">\n'
      xml += '  <diagram name="ER Diagram" id="er-diagram">\n'
      xml += '    <mxGraphModel dx="1422" dy="794" grid="1" gridSize="10" guides="1" tooltips="1" connect="1" arrows="1" fold="1" page="1" pageScale="1" pageWidth="3000" pageHeight="3000" math="0" shadow="0">\n'
      xml += '      <root>\n'
      xml += '        <mxCell id="0" />\n'
      xml += '        <mxCell id="1" parent="0" />\n'

      let cellId = 2

      // 导出实体节点
      this.entities.forEach(entity => {
        if (!entity.visible) return
        xml += `        <mxCell id="${cellId}" value="${this.escapeXml(entity.name)}" style="rounded=0;whiteSpace=wrap;html=1;strokeColor=#000000;fillColor=#ffffff;fontFamily=宋体;fontSize=22;" vertex="1" parent="1">\n`
        xml += `          <mxGeometry x="${entity.x}" y="${entity.y}" width="${entity.width}" height="${entity.height}" as="geometry" />\n`
        xml += '        </mxCell>\n'
        entity.drawioId = cellId
        cellId++
      })

      // 导出属性节点
      this.attributes.forEach(attr => {
        if (!attr.visible) return
        const x = attr.x - attr.rx
        const y = attr.y - attr.ry
        const width = attr.rx * 2
        const height = attr.ry * 2
        // 主键添加下划线样式 - draw.io 使用 fontStyle=4 表示下划线
        const fontStyle = attr.isPrimaryKey ? 'fontStyle=4;' : ''
        xml += `        <mxCell id="${cellId}" value="${this.escapeXml(attr.name)}" style="ellipse;whiteSpace=wrap;html=1;strokeColor=#000000;fillColor=#ffffff;fontFamily=宋体;fontSize=20;${fontStyle}" vertex="1" parent="1">\n`
        xml += `          <mxGeometry x="${x}" y="${y}" width="${width}" height="${height}" as="geometry" />\n`
        xml += '        </mxCell>\n'
        attr.drawioId = cellId
        cellId++
      })

      // 导出连接线
      this.links.forEach(link => {
        const attrKey = link.id.replace('link-', '')
        const attr = this.attributes.find(a => a.key === attrKey)
        if (!attr || !attr.visible) return
        
        const entity = this.entities.find(e => e.key === attr.entityKey)
        if (!entity || !entity.visible) return

        xml += `        <mxCell id="${cellId}" value="" style="endArrow=none;html=1;strokeColor=#000000;" edge="1" parent="1" source="${entity.drawioId}" target="${attr.drawioId}">\n`
        xml += '          <mxGeometry relative="1" as="geometry" />\n'
        xml += '        </mxCell>\n'
        cellId++
      })

      xml += '      </root>\n'
      xml += '    </mxGraphModel>\n'
      xml += '  </diagram>\n'
      xml += '</mxfile>'

      // 下载文件
      const blob = new Blob([xml], { type: 'application/xml' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      const now = new Date()
      const ts = `${now.getFullYear()}${String(now.getMonth()+1).padStart(2,'0')}${String(now.getDate()).padStart(2,'0')}`
      a.download = `${ts}ER图.drawio`
      a.href = url
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      this.$message.success('导出成功！可以使用 Draw.io 打开编辑')
    },

    escapeXml(text) {
      return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;')
    },

    exportToImage(options) {
      const svgElement = this.$el.querySelector('.diagram-svg')
      if (!svgElement) return

      // 克隆 SVG 以避免修改原始元素
      const clonedSvg = svgElement.cloneNode(true)
      
      // 计算实际内容的边界
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
      
      this.entities.forEach(entity => {
        if (!entity.visible) return
        minX = Math.min(minX, entity.x)
        minY = Math.min(minY, entity.y)
        maxX = Math.max(maxX, entity.x + entity.width)
        maxY = Math.max(maxY, entity.y + entity.height)
      })
      
      this.attributes.forEach(attr => {
        if (!attr.visible) return
        minX = Math.min(minX, attr.x - attr.rx)
        minY = Math.min(minY, attr.y - attr.ry)
        maxX = Math.max(maxX, attr.x + attr.rx)
        maxY = Math.max(maxY, attr.y + attr.ry)
      })
      
      // 添加边距
      const padding = 50
      minX -= padding
      minY -= padding
      maxX += padding
      maxY += padding
      
      const contentWidth = maxX - minX
      const contentHeight = maxY - minY
      
      // 设置 viewBox 为实际内容区域
      clonedSvg.setAttribute('viewBox', `${minX} ${minY} ${contentWidth} ${contentHeight}`)
      clonedSvg.setAttribute('width', contentWidth)
      clonedSvg.setAttribute('height', contentHeight)
      
      const svgData = new XMLSerializer().serializeToString(clonedSvg)
      
      if (options.format === 'svg') {
        const blob = new Blob([svgData], { type: 'image/svg+xml' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        const now = new Date()
        const ts = `${now.getFullYear()}${String(now.getMonth()+1).padStart(2,'0')}${String(now.getDate()).padStart(2,'0')}`
        a.download = `${ts}ER图.svg`
        a.href = url
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
        this.$message.success('导出成功！')
        return
      }

      // PNG/JPEG 导出
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      const img = new Image()
      
      // 使用 2倍或更高的分辨率以提高清晰度
      const scale = 3 // 3倍分辨率
      
      img.onload = () => {
        canvas.width = contentWidth * scale
        canvas.height = contentHeight * scale
        
        // 设置高质量渲染
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        
        if (options.background === 'white') {
          ctx.fillStyle = 'white'
          ctx.fillRect(0, 0, canvas.width, canvas.height)
        }
        
        // 缩放上下文以匹配高分辨率
        ctx.scale(scale, scale)
        ctx.drawImage(img, 0, 0, contentWidth, contentHeight)
        
        canvas.toBlob((blob) => {
          const url = URL.createObjectURL(blob)
          const a = document.createElement('a')
          const now = new Date()
          const ts = `${now.getFullYear()}${String(now.getMonth()+1).padStart(2,'0')}${String(now.getDate()).padStart(2,'0')}`
          a.download = `${ts}ER图.${options.format}`
          a.href = url
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          URL.revokeObjectURL(url)
          this.$message.success('导出成功！')
        }, `image/${options.format}`, 0.95) // 设置质量为 95%
      }
      
      img.onerror = (e) => {
        console.error('图片加载失败:', e)
        this.$message.error('导出失败，请重试')
      }
      
      img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)))
    },

    toggleFullscreen() {
      if (!document.fullscreenElement) {
        this.$refs.diagramContainer.requestFullscreen()
        this.isFullscreen = true
      } else {
        document.exitFullscreen()
        this.isFullscreen = false
      }
    },

    openEntitySelectDialog() {
      this.availableEntities = this.entities.map(e => ({ key: e.key, name: e.name }))
      this.selectedEntities = this.entities.filter(e => e.visible).map(e => e.key)
      this.isEntitySelectVisible = true
    },

    selectAllEntities() {
      this.selectedEntities = [...this.availableEntities.map(e => e.key)]
    },

    invertSelection() {
      const all = this.availableEntities.map(e => e.key)
      this.selectedEntities = all.filter(k => !this.selectedEntities.includes(k))
    },

    applyEntityFilter() {
      // 使用 visible 属性控制显示/隐藏，而不是删除
      this.entities.forEach(entity => {
        entity.visible = this.selectedEntities.includes(entity.key)
      })
      
      this.attributes.forEach(attr => {
        attr.visible = this.selectedEntities.includes(attr.entityKey)
      })
      
      this.isEntitySelectVisible = false
      this.$message.success(`已筛选 ${this.selectedEntities.length} 个实体`)
    }
  }
}
</script>

<style scoped>
.diagram-output {
  background: #FFFFFF;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 空状态样式 */
.empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  background: #fafafa;
}

.empty-state svg {
  color: #d1d5db;
  margin-bottom: 20px;
}

.empty-title {
  font-size: 14px;
  font-weight: normal;
  margin: 0 0 8px 0;
}

.empty-description {
  font-size: 14px;
  font-weight: normal;
  margin: 0;
  text-align: center;
  line-height: 1.6;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 24px;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
  min-height: 50px;
}

.toolbar-left { display: flex; gap: 10px; align-items: center; }
.toolbar-right { display: flex; gap: 10px; }

.canvas-hint-toolbar {
  font-size: 14px;
  color: #856404;
  background: #fff3cd;
  padding: 6px 14px;
  border-radius: 4px;
  white-space: nowrap;
}

.tool-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  font-size: 14px;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;
}

.tool-btn i { font-size: 15px; }

.btn-purple { background: #8c6ff7; }
.btn-purple:hover { background: #7559d4; }
.btn-red { background: #ff6b6b; }
.btn-red:hover { background: #e55c5c; }
.btn-orange { background: #f59e0b; }
.btn-orange:hover { background: #d97706; }
.btn-blue { background: #409eff; }
.btn-blue:hover { background: #2b88e6; }

.diagram-wrapper {
  flex: 1;
  background-color: #F2F2F7;
  position: relative;
  overflow: hidden;
}

.diagram-container {
  width: 100%;
  height: calc(100vh - 170px);
  background-color: #FFFFFF;
  position: relative;
  border: 1px solid #E4E7ED;
  overflow: hidden;
}

.diagram-svg {
  width: 100%;
  height: 100%;
  cursor: grab;
  user-select: none;
}

.diagram-svg.panning {
  cursor: grabbing;
}

.draggable-node {
  cursor: move;
}

.draggable-node:hover {
  filter: brightness(0.95);
}

.resize-handle {
  cursor: nwse-resize;
}

.resize-handle:hover {
  r: 8;
}

.entity-select-header {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-bottom: 12px;
}

.entity-checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 300px;
  overflow-y: auto;
}

.dialog-footer { display: flex; justify-content: flex-end; gap: 8px; }
</style>
